import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import { ItemCard } from 'tradera/components/item-card/item-card';
import { generateItemCardPlaceholders } from 'tradera/components/item-card/generate-item-card-placeholders';
import * as itemActions from 'tradera/state/items/actions';
import uiActions from 'tradera/state/ui/actions';
import { Carousel } from 'tradera/components/carousel/carousel';
import {
    LeftButton,
    RightButton
} from 'tradera/components/carousel/carousel-buttons';
import { CarouselContent } from 'tradera/components/carousel/carousel-content';
import {
    selectAutomaticTranslationPreferred,
    selectPreferredLanguageCode
} from 'tradera/state/language/selectors';
import {
    getRecommendationsQueryParams,
    useGetRecommendationsQuery
} from 'tradera/state/services/discover-api';
import { selectShippingRegionCountryCodeIso2 } from 'tradera/state/shipping-region/selectors';

export const RecommendationsCarousel = ({
    slot,
    onClickMore,
    gtmLabel,
    moreResultsCard,
    setError,
    ...props
}) => {
    const dispatch = useDispatch();

    const userLanguage = useSelector(selectPreferredLanguageCode);
    const automaticTranslationPreferred = useSelector(
        selectAutomaticTranslationPreferred
    );
    const shippingCountryCode = useSelector(
        selectShippingRegionCountryCodeIso2
    );

    const { data = {}, isLoading } = useGetRecommendationsQuery(
        getRecommendationsQueryParams({
            slot,
            userLanguage,
            automaticTranslationPreferred,
            shippingCountryCode
        }),
        { skip: !slot }
    );

    const { items = [] } = data;

    const isMobile = useIsMobile();
    const loadingItemsCount = isMobile ? 2 : 6;

    if (!isLoading && items.length === 0) {
        setError();
    }

    const displayItems = isLoading
        ? generateItemCardPlaceholders(loadingItemsCount)
        : items;

    const toggleWishList = (itemId) => {
        dispatch(itemActions.wishlist(itemId));
    };

    const openQuickView = (item) =>
        dispatch(
            uiActions.openQuickViewAction({
                items,
                item,
                openedFrom: gtmLabel
            })
        );

    // To indicate that the list is scrollable on mobile we show 2.5 items
    const numberCarouselCards = isMobile ? 2.5 : 6;

    return (
        <section {...props}>
            <Carousel
                id="recommendations-carousel"
                numberItemsToShow={numberCarouselCards}
                numberItemsToScroll={numberCarouselCards}
                padX
                className="mx-md-3 mx-lg-2 mx-xl-0">
                <LeftButton size="md" alignVerticallyToItemCards />
                <CarouselContent>
                    {displayItems.map((item) => (
                        <ItemCard
                            // This is to make the parent container set grid gaps instead
                            // of individual cards keeping paddings and margins set of their own.
                            // Makes displaying cards in different situations more predictable.
                            className="m-0 p-0"
                            item={item}
                            onAddToWishlist={toggleWishList}
                            onQuickView={openQuickView}
                            onRemoveFromWishlist={toggleWishList}
                            showTitleOnOneLine
                            gaCategory="Startpage picks for you"
                            key={item.itemId}
                        />
                    ))}
                    {moreResultsCard}
                </CarouselContent>
                <RightButton size="md" alignVerticallyToItemCards />
            </Carousel>
        </section>
    );
};
